import { useState } from 'react'
import { useUsers, useApproveUser, useRejectUser, type AdminUser } from '../lib/admin'
import { useToast } from '../components/ToastContainer'
import Modal from '../components/Modal'

export default function AdminUsers() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const { data: users, isLoading, error } = useUsers(statusFilter)
  const approveMutation = useApproveUser()
  const rejectMutation = useRejectUser()
  const { success: toastSuccess, error: toastError } = useToast()

  const [rejectTarget, setRejectTarget] = useState<AdminUser | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [confirmingReject, setConfirmingReject] = useState(false)

  const filters: { label: string; value: string | undefined }[] = [
    { label: 'Todos', value: undefined },
    { label: 'Pendentes', value: 'pending' },
    { label: 'Aprovados', value: 'approved' },
    { label: 'Rejeitados', value: 'rejected' },
  ]

  const handleApprove = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja aprovar este usuário?')) return
    try {
      await approveMutation.mutateAsync(userId)
      toastSuccess('Usuário aprovado com sucesso!')
    } catch (err: any) {
      toastError(err?.response?.data?.detail ?? 'Erro ao aprovar usuário')
    }
  }

  const openReject = (user: AdminUser) => {
    setRejectTarget(user)
    setRejectReason('')
    setConfirmingReject(true)
  }

  const closeReject = () => {
    setRejectTarget(null)
    setRejectReason('')
    setConfirmingReject(false)
  }

  const handleReject = async () => {
    if (!rejectTarget) return
    try {
      await rejectMutation.mutateAsync({ userId: rejectTarget.id, reason: rejectReason })
      toastSuccess('Usuário rejeitado com sucesso!')
      closeReject()
    } catch (err: any) {
      toastError(err?.response?.data?.detail ?? 'Erro ao rejeitar usuário')
    }
  }

  const isActionable = (status: string) => status === 'pending'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (error) {
    return <p className="text-[var(--color-danger)]">Erro ao carregar usuários</p>
  }

  const list = users ?? []

  if (list.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--color-text-muted)]">Nenhum usuário encontrado</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-[var(--color-text)]">Todos os Usuários</h2>

      <div className="flex gap-2">
        {filters.map((f) => {
          const active = statusFilter === f.value
          return (
            <button
              key={f.label}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1 text-sm rounded-lg ${active
                ? 'bg-[var(--color-brand-500)] text-white'
                : 'bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      <div className="divide-y divide-[var(--color-border)]">
        {list.map((user) => (
          <div key={user.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-[var(--color-text)]">
                  {user.full_name || 'Sem nome'}
                </h3>
                <p className="text-sm text-[var(--color-text-muted)]">{user.email}</p>
                <div className="mt-1 flex gap-4 text-xs text-[var(--color-text-muted)]">
                  <span>ID: {user.id.slice(0, 8)}...</span>
                  <span>Criado: {new Date(user.created_at).toLocaleDateString('pt-BR')}</span>
                  {user.approved_at && (
                    <span>Aprovado: {new Date(user.approved_at).toLocaleDateString('pt-BR')}</span>
                  )}
                  {user.rejected_at && (
                    <span>Rejeitado: {new Date(user.rejected_at).toLocaleDateString('pt-BR')}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={user.status} />
                {isActionable(user.status) && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(user.id)}
                      disabled={approveMutation.isPending}
                      className="rounded-lg bg-[var(--color-success-500)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-success-600)] disabled:opacity-50"
                    >
                      Aprovar
                    </button>
                    <button
                      onClick={() => openReject(user)}
                      disabled={rejectMutation.isPending}
                      className="rounded-lg bg-[var(--color-danger-500)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-danger-600)] disabled:opacity-50"
                    >
                      Rejeitar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {confirmingReject && rejectTarget && (
        <Modal isOpen={true} onClose={closeReject} title="Rejeitar usuário">
          <div className="space-y-4">
            <p className="text-sm text-[var(--color-text-muted)]">
              Tem certeza que deseja rejeitar <strong>{rejectTarget.full_name || rejectTarget.email}</strong>?
            </p>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)]">
                Motivo (opcional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Ex: Dados incorretos, duplicata, outra razão..."
                className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-500)] focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeReject}
                className="rounded-lg border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="rounded-lg bg-[var(--color-danger-500)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-danger-600)] disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejeitando...' : 'Rejeitar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config =
    status === 'pending'
      ? { bg: 'bg-[var(--color-warning-100)]', text: 'text-[var(--color-warning-700)]', label: 'Pendente' }
      : status === 'approved'
      ? { bg: 'bg-[var(--color-success-100)]', text: 'text-[var(--color-success-700)]', label: 'Aprovado' }
      : { bg: 'bg-[var(--color-danger-100)]', text: 'text-[var(--color-danger-700)]', label: 'Rejeitado' }

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  )
}
